import { SaSmsGuard } from '@components/sa-sms-messaging/sa-sms-guard';
import { SaSmsNav } from '@components/sa-sms-messaging/sa-sms-nav';
import { SmsBillingSettingsForm } from '@components/sa-sms-messaging/sms-billing-settings-form';
import { SmsPackageEditor } from '@components/sa-sms-messaging/sms-package-editor';
import { SmsPlanCreditsEditor } from '@components/sa-sms-messaging/sms-plan-credits-editor';
import { SmsPlatformSettingsForm } from '@components/sa-sms-messaging/sms-platform-settings-form';
import { ISaSmsSettings } from '@interfaces/sa-sms';
import { Alert, Col, Row, Spin } from 'antd';
import { useSaSmsErrorText, useSaSmsGet } from 'hooks/use-sa-sms-api';
import { useTranslation } from 'react-i18next';

/** Super-admin SMS console: platform settings. A part whose source failed is reported and the rest still renders. */
export const SaSmsSettings: React.FC = () => {
    const { t } = useTranslation(['sa-sms']);
    const errorText = useSaSmsErrorText();
    const { data, isLoading, isError, error, refetch } = useSaSmsGet<ISaSmsSettings>('/settings');
    const changed = () => refetch();
    return (
        <SaSmsGuard>
            {(canEdit) => (
                <section className="sa-sms-page">
                    <SaSmsNav active="settings" />
                    {isLoading && <Spin style={{ display: 'block', margin: '32px auto' }} />}
                    {isError && <Alert type="error" showIcon message={errorText(error)} />}
                    {data &&
                        Object.entries(data.errors).map(([part, message]) => (
                            <Alert
                                key={part}
                                type="warning"
                                showIcon
                                style={{ marginBottom: 16 }}
                                message={t('settings.partFailed', {
                                    part: t(`settings.parts.${part}`, { defaultValue: part }),
                                    message,
                                })}
                            />
                        ))}
                    {data && (
                        <Row gutter={[16, 16]}>
                            {data.platform && (
                                <Col span={24}>
                                    <SmsPlatformSettingsForm
                                        platform={data.platform}
                                        canEdit={canEdit}
                                        onChanged={changed}
                                    />
                                </Col>
                            )}
                            {data.packages && (
                                <Col xs={24} xl={12}>
                                    <SmsPackageEditor
                                        packages={data.packages}
                                        canEdit={canEdit}
                                        onChanged={changed}
                                    />
                                </Col>
                            )}
                            {data.plans && (
                                <Col xs={24} xl={12}>
                                    <SmsPlanCreditsEditor
                                        plans={data.plans}
                                        canEdit={canEdit}
                                        onChanged={changed}
                                    />
                                </Col>
                            )}
                            {data.billing && (
                                <Col span={24}>
                                    <SmsBillingSettingsForm
                                        billing={data.billing}
                                        canEdit={canEdit}
                                        onChanged={changed}
                                    />
                                </Col>
                            )}
                        </Row>
                    )}
                </section>
            )}
        </SaSmsGuard>
    );
};
